import BackendService from 'BackendService';
import { useEffect, useState, type Dispatch, type RefObject, type SetStateAction } from 'react';
import type UploadItem from '../../../components/UploadItem';
import type Investor from '../../../models/Investor';

async function uploadDocument(
  backend: BackendService,
  investor: Investor | undefined,
  fundOpportunityId: string,
  item: UploadItem,
  defaultCategoryId: string | undefined,
) {
  const fileExtension = item.file.name.split('.').pop() || 'pdf';
  const location = investor
    ? await backend.uploadAssetContent(
        'documents',
        investor.id,
        'Investor',
        item.title,
        item.file,
        fileExtension,
      )
    : await backend.uploadAssetContent(
        'documents',
        fundOpportunityId,
        'FundOpportunity',
        item.title,
        item.file,
        fileExtension,
      );
  const document = {
    title: item.title,
    location: location.replace(/^\/api\//, ''),
    categoryId: item.categoryId ?? defaultCategoryId,
  };
  if (investor) {
    await backend.addInvestorDocument(investor.id, document);
  } else {
    await backend.addGenericInvestorDocument(fundOpportunityId, document);
  }
  return location;
}

async function uploadRows(
  rows: UploadItem[],
  setUploadItems: Dispatch<SetStateAction<UploadItem[]>>,
  backend: BackendService,
  investor: Investor | undefined,
  fundOpportunityId: string,
  defaultCategoryId: string | undefined,
) {
  return Promise.all(
    rows.map(async (item, index) => {
      try {
        const location = await uploadDocument(
          backend,
          investor,
          fundOpportunityId,
          item,
          defaultCategoryId,
        );
        setUploadItems((prev) =>
          prev.map((row, i) => (i === index ? { ...row, status: 'done' as const, location } : row)),
        );
        return true;
      } catch {
        setUploadItems((prev) =>
          prev.map((row, i) => (i === index ? { ...row, status: 'error' as const } : row)),
        );
        return false;
      }
    }),
  );
}

function createUploadItems(files: File[], defaultCategoryId?: string): UploadItem[] {
  return files.map((file) => ({
    file,
    title: file.name.replace(/\.[^/.]+$/, ''),
    status: 'pending',
    categoryId: defaultCategoryId,
  }));
}

export function useInvestorDocumentUpload({
  backendRef,
  investor,
  fundOpportunityId,
  defaultCategoryId,
  loadExistingDocs,
  loadCategories,
  onDocumentsAdded,
}: {
  backendRef: RefObject<BackendService>;
  investor?: Investor;
  fundOpportunityId: string;
  defaultCategoryId?: string;
  loadExistingDocs: () => Promise<void>;
  loadCategories: () => Promise<void>;
  onDocumentsAdded?: () => void;
}) {
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadItems, setUploadItems] = useState<UploadItem[]>([]);

  useEffect(() => {
    if (!defaultCategoryId) return;
    setUploadItems((items) =>
      items.some((item) => !item.categoryId)
        ? items.map((item) => (item.categoryId ? item : { ...item, categoryId: defaultCategoryId }))
        : items,
    );
  }, [defaultCategoryId]);

  const handleFilesSelected = (files: File[]) => {
    if (!files?.length) return;
    setUploadItems((prev) => [...prev, ...createUploadItems(files, defaultCategoryId)]);
    setUploadModalOpen(true);
  };

  const setRowCategory = (idx: number, categoryId: string) => {
    setUploadItems((items) => items.map((item, i) => (i === idx ? { ...item, categoryId } : item)));
  };
  const applyCategoryToAll = (categoryId: string) => {
    setUploadItems((items) => items.map((item) => ({ ...item, categoryId })));
  };

  const uploadAll = async () => {
    const rows = uploadItems;
    setUploadItems((prev) => prev.map((item) => ({ ...item, status: 'uploading' as const })));
    const outcomes = await uploadRows(
      rows,
      setUploadItems,
      backendRef.current!,
      investor,
      fundOpportunityId,
      defaultCategoryId,
    );
    await loadExistingDocs();
    await loadCategories();
    onDocumentsAdded?.();
    if (outcomes.every(Boolean)) {
      setUploadItems([]);
      setUploadModalOpen(false);
    }
  };

  return {
    uploadModalOpen,
    setUploadModalOpen,
    uploadItems,
    setUploadItems,
    handleFilesSelected,
    setRowCategory,
    applyCategoryToAll,
    uploadAll,
  };
}
